import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Check, ArrowRight, Crown, Zap, Building2, ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLAN_ICONS = { starter: Zap, pro: Crown, business: Building2 };

export default function PricingPage() {
  const { user, login, loading: authLoading } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loadingPlan, setLoadingPlan] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/payments/plans`).then((r) => setPlans(r.data.plans)).catch(() => {});
  }, []);

  const handleSubscribe = async (plan) => {
    if (!user) {
      login();
      return;
    }
    setLoadingPlan(plan.id);
    try {
      const res = await axios.post(
        `${API}/payments/checkout`,
        { lookup_key: plan.lookup_key, origin_url: window.location.origin },
        { withCredentials: true }
      );
      window.location.href = res.data.checkout_url;
    } catch (err) {
      if (err.response?.status === 401) {
        login();
      }
    } finally {
      setLoadingPlan(null);
    }
  };

  return (
    <div className="min-h-screen relative" style={{ background: "#05030A" }}>
      <div className="noise-overlay" />
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        <div className="absolute -top-[30%] -right-[20%] w-[800px] h-[800px] rounded-full bg-[#9D00FF] opacity-[0.04] blur-[120px]" />
        <div className="absolute -bottom-[20%] -left-[15%] w-[600px] h-[600px] rounded-full bg-[#FF007F] opacity-[0.04] blur-[120px]" />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-6 py-16 sm:py-24">
        <button
          onClick={() => navigate("/")}
          data-testid="pricing-back-button"
          className="flex items-center gap-2 text-sm text-[#A19BB0] hover:text-white transition-colors duration-200 mb-12"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to scanner
        </button>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <p className="text-xs font-bold tracking-[0.25em] uppercase text-fuchsia-400 mb-4">Pricing</p>
          <h1 className="text-4xl sm:text-5xl font-black font-['Outfit'] tracking-tighter text-white mb-4">
            Monitor your SEO, <span className="gradient-text">automatically</span>
          </h1>
          <p className="text-base text-[#A19BB0] max-w-lg mx-auto">
            Weekly scans, instant alerts when issues arise, and actionable reports. All plans include a 14-day free trial.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {plans.map((plan, i) => {
            const Icon = PLAN_ICONS[plan.id] || Zap;
            return (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className={`glass-card rounded-2xl p-8 flex flex-col relative ${plan.popular ? "border-fuchsia-500/40" : ""}`}
                data-testid={`pricing-plan-${plan.id}`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="text-[10px] font-bold tracking-[0.15em] uppercase px-3 py-1 rounded-full bg-gradient-to-r from-[#9D00FF] to-[#FF007F] text-white">
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#FF007F]/20 to-[#9D00FF]/20 border border-white/10 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-fuchsia-400" />
                  </div>
                  <div>
                    <h3 className="font-['Outfit'] text-lg font-bold text-white">{plan.name}</h3>
                    <p className="text-xs text-[#A19BB0]">{plan.websites} {plan.websites === 1 ? "website" : "websites"}</p>
                  </div>
                </div>

                <div className="mb-6">
                  <span className="text-4xl font-black font-['Outfit'] text-white">${plan.price}</span>
                  <span className="text-sm text-[#A19BB0]">/month</span>
                </div>

                <ul className="flex-1 space-y-3 mb-8">
                  {plan.features?.map((f, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm text-[#A19BB0]">
                      <Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handleSubscribe(plan)}
                  disabled={loadingPlan === plan.id}
                  data-testid={`subscribe-${plan.id}-button`}
                  className={`w-full h-12 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-opacity duration-200 disabled:opacity-50 ${
                    plan.popular
                      ? "bg-gradient-to-r from-[#9D00FF] to-[#FF007F] text-white hover:opacity-90"
                      : "bg-white/5 border border-white/10 text-white hover:bg-white/10"
                  }`}
                >
                  {loadingPlan === plan.id ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      {user ? "Start Free Trial" : "Sign in to Start"}
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </motion.div>
            );
          })}
        </div>

        {!authLoading && !user && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-center text-sm text-[#A19BB0] mt-8"
          >
            Sign in with Google to start your 14-day free trial. No credit card required during trial.
          </motion.p>
        )}
      </div>
    </div>
  );
}
